import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ErrorService } from '../../error';
import {
  InsuranceClaimFormModel,
  InsuranceClaimModel,
  InsuranceClaimRejectReasonModel,
  InsuranceClaimStatus,
  InsuranceClaimStatusFlow,
  InsuranceClaimStatusHistoryModel,
} from './types';
import { Action } from '../view/types';
import { catchError } from 'rxjs/operators';
import { CrudType } from '../../service/message/types';
import { MessageService } from '../../service/message/message.service';

@Injectable({
  providedIn: 'root',
})
export class InsuranceClaimApiService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly errorService: ErrorService,
    private readonly messageService: MessageService,
  ) {}

  getById = (id: number): Observable<InsuranceClaimModel> => {
    let url = this.formatUrl([id.toString()]);
    return this.httpClient.get<InsuranceClaimModel>(url).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  getByUniqueId = (uniqueId: string): Observable<InsuranceClaimModel> => {
    let url = this.formatUrl(['uniqueid', uniqueId]);
    return this.httpClient.get<InsuranceClaimModel>(url).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  createInstance = (
    contractId: number,
  ): Observable<InsuranceClaimFormModel> => {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('contractId', contractId.toString());

    let url = this.formatUrl(['create']);
    return this.httpClient
      .get<InsuranceClaimFormModel>(url, { params: queryParams })
      .pipe(
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  add = (
    insuranceClaimModel: InsuranceClaimFormModel,
  ): Observable<InsuranceClaimModel> => {
    let url = this.formatUrl([]);
    return this.httpClient
      .post<InsuranceClaimModel>(url, insuranceClaimModel, {
        observe: 'response',
      })
      .pipe(
        this.messageService.checkStatusCustomOperator(CrudType.create),
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  update = (
    insuranceClaimModel: InsuranceClaimFormModel,
  ): Observable<InsuranceClaimModel> => {
    let url = this.formatUrl([insuranceClaimModel.id.toString()]);
    return this.httpClient
      .put<InsuranceClaimModel>(url, insuranceClaimModel, {
        observe: 'response',
      })
      .pipe(
        this.messageService.checkStatusCustomOperator(CrudType.update),
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  // Creates the document requests expected by the claim type. Idempotent: an already requested
  // document type is skipped server side, so clicking twice never duplicates a request card.
  generateDocuments = (insuranceClaimId: number): Observable<boolean> => {
    let url = this.formatUrl([insuranceClaimId.toString(), 'documents']);
    return this.httpClient
      .post<boolean>(url, '', {
        observe: 'response',
      })
      .pipe(
        this.messageService.checkStatusCustomOperator(CrudType.create),
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  // Status transitions: the API answers 204 NoContent, so the body is empty — callers rely on the
  // getByUniqueId refresh dispatched by the effects, not on the returned value.
  requestValidation = (insuranceClaimId: number): Observable<boolean> => {
    let url = this.formatUrl([
      'requestvalidation',
      insuranceClaimId.toString(),
    ]);
    return this.httpClient.put<boolean>(url, insuranceClaimId).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  validate = (insuranceClaimId: number): Observable<boolean> => {
    let url = this.formatUrl(['validate', insuranceClaimId.toString()]);
    return this.httpClient.put<boolean>(url, insuranceClaimId).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  openClaim = (insuranceClaimId: number): Observable<boolean> => {
    let url = this.formatUrl(['openClaim', insuranceClaimId.toString()]);
    return this.httpClient.put<boolean>(url, insuranceClaimId).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  openDossierToTransmitter = (
    insuranceClaimId: number,
  ): Observable<boolean> => {
    let url = this.formatUrl([
      'openDossierToTransmitter',
      insuranceClaimId.toString(),
    ]);
    return this.httpClient.put<boolean>(url, insuranceClaimId).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  close = (insuranceClaimId: number): Observable<boolean> => {
    let url = this.formatUrl(['close', insuranceClaimId.toString()]);
    return this.httpClient.put<boolean>(url, insuranceClaimId).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  reopen = (insuranceClaimId: number): Observable<boolean> => {
    let url = this.formatUrl(['reopen', insuranceClaimId.toString()]);
    return this.httpClient.put<boolean>(url, insuranceClaimId).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  reject = (
    insuranceClaimRejectReasonModel: InsuranceClaimRejectReasonModel,
  ): Observable<boolean> => {
    let url = this.formatUrl([
      'reject',
      insuranceClaimRejectReasonModel.insuranceClaimId.toString(),
    ]);
    return this.httpClient
      .put<boolean>(url, insuranceClaimRejectReasonModel)
      .pipe(
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  getStatusHistory = ({
    insuranceClaimId,
    insuranceClaimStatusHistoryId,
  }: {
    insuranceClaimId: number;
    insuranceClaimStatusHistoryId: number;
  }): Observable<InsuranceClaimStatusHistoryModel> => {
    let url = this.formatUrl([
      insuranceClaimId.toString(),
      'insuranceClaimStatusHistory',
      insuranceClaimStatusHistoryId.toString(),
    ]);
    return this.httpClient.get<InsuranceClaimStatusHistoryModel>(url).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  updateStatusHistory = (
    insuranceClaimStatusHistoryModel: InsuranceClaimStatusHistoryModel,
  ): Observable<InsuranceClaimStatusHistoryModel> => {
    let url = this.formatUrl([
      insuranceClaimStatusHistoryModel.insuranceClaimId.toString(),
      'insuranceClaimStatusHistory',
      insuranceClaimStatusHistoryModel.id.toString(),
    ]);
    return this.httpClient
      .put<InsuranceClaimStatusHistoryModel>(
        url,
        insuranceClaimStatusHistoryModel,
        {
          observe: 'response',
        },
      )
      .pipe(
        this.messageService.checkStatusCustomOperator(CrudType.update),
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  deleteStatusHistory = ({
    insuranceClaimId,
    insuranceClaimStatusHistoryId,
  }: {
    insuranceClaimId: number;
    insuranceClaimStatusHistoryId: number;
  }): Observable<boolean> => {
    let url = this.formatUrl([
      insuranceClaimId.toString(),
      'insuranceClaimStatusHistory',
      insuranceClaimStatusHistoryId.toString(),
    ]);
    return this.httpClient.delete<boolean>(url).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  getStatusFlow = (
    status: InsuranceClaimStatus,
  ): Observable<InsuranceClaimStatusFlow[]> => {
    let url = this.formatUrl(['statusFlow', status]);
    return this.httpClient.get<InsuranceClaimStatusFlow[]>(url).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  getStatusActions = (status: string): Observable<Action[]> => {
    let url = this.formatUrl(['statusActions', status]);
    return this.httpClient.get<Action[]>(url).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  formatUrl = (parameters: string[]): string => {
    var url = environment.comparanooapiurl;
    url += environment.features.insuranceClaim.url;
    parameters.forEach((parameters) => {
      url += '/';
      url += parameters;
    });
    return url;
  };
}
